import {
  Box,
  Chip,
  Container,
  DjangoTableWithPagination,
  IColumn,
  InternalLink,
  Spacer,
  TableBody,
  TableCell,
  TableRow,
  Title,
  TextField,
  Tooltip,
  useDjangoPaginatedQuery,
  useTheme,
} from '@color/continuum';
import { Palette } from '@mui/material/styles';
import { omit, partial } from 'lodash-es';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom-v5-compat';

import { QC_METRICS_BATCH_PATH } from '../paths';
import { hashNameIntoPaletteColor } from '../utils';

interface QCMetricsBatch {
  batch_label: string;
  assay_type: string;
  tag_stats: Record<string, number>;
  state_counts: Record<string, number>;
  sample_sequence_run_count: number;
  on_hold_count: number;
}

const COLUMNS: IColumn[] = [
  { id: 'batch_label', name: 'Batch Label', isSortable: false },
  { id: 'assay_type', name: 'Assay Type', isSortable: false, widthRatio: 0.1 },
  { id: 'tags', name: 'Tags', isSortable: false },
  { id: 'states', name: 'States', isSortable: false },
  { id: 'on_hold_count', name: 'On Hold', isSortable: false, widthRatio: 0.1 },
];

function renderCountDistribution(
  counts: Record<string, number>,
  total: number,
  getColor: (name: string) => string = hashNameIntoPaletteColor
) {
  const bars: [string, number, string][] = Object.entries(counts)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, count]) => [name, count / total, `${count} out of ${total}`]);

  return (
    <>
      {bars.map(([name, fraction, tooltip]) => (
        <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={tooltip} placement="left">
            <Box
              key={name}
              sx={{
                width: `${fraction * 100}%`,
                backgroundColor: getColor(name),
                paddingBlock: 0.5,
                borderRadius: 0.5,
              }}
            />
          </Tooltip>
          <Tooltip title={tooltip} placement="left">
            <Box sx={{ flexShrink: 0 }}>{name}</Box>
          </Tooltip>
        </Box>
      ))}
    </>
  );
}

const getStateColor = (themePalette: Palette, state: string): string => {
  if (state === 'approved') {
    return themePalette.success.main;
  }
  if (state === 'rejected') {
    return themePalette.error.main;
  }
  if (state === 'new') {
    return themePalette.error.main;
  }
  if (state === 'sequenced') {
    return themePalette.warning.main;
  }
  if (state === 'variants_called') {
    return themePalette.primary.main;
  }
  if (state === 'pending_controls') {
    return themePalette.warning.main;
  }
  return themePalette.info.main;
};

const KIT_BARCODE_FILTER = 'sample__kit_barcode__icontains';
const KIT_BARCODE_QUERY_PARAM = 'kit_barcode';

export const View = () => {
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Record<string, string>>(
    searchParams.get(KIT_BARCODE_QUERY_PARAM)
      ? { [KIT_BARCODE_FILTER]: searchParams.get(KIT_BARCODE_QUERY_PARAM)! }
      : {}
  );

  const { data, isLoading, isFetching, paginationOptions, tableOptions } =
    useDjangoPaginatedQuery<QCMetricsBatch>('/api/v1/qc_metrics/batches', {
      initialRowsPerPage: searchParams.get('page_size')
        ? Number(searchParams.get('page_size'))
        : 10,
      initialPageNumber: searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0,
      additionalQueryParams: filters,
    });

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex' }}>
        <Title>QC Metrics: Batches</Title>
        <Box sx={{ marginLeft: 'auto', width: 200 }}>
          <TextField
            label="Kit Barcode"
            onChange={({ target: { value } }) => {
              if (value) {
                setFilters((prev) => ({ ...prev, [KIT_BARCODE_FILTER]: value }));
                setSearchParams((params) => {
                  params.set(KIT_BARCODE_QUERY_PARAM, value);
                  return params;
                });
              } else {
                setFilters((prev) => omit(prev, KIT_BARCODE_FILTER));
                setSearchParams((params) => {
                  params.delete(KIT_BARCODE_QUERY_PARAM);
                  return params;
                });
              }
            }}
            optionalIndicatorIsEnabled={false}
          />
        </Box>
      </Box>
      <Spacer variant="large" />
      <DjangoTableWithPagination
        columns={COLUMNS}
        isLoading={isLoading || isFetching}
        tableOptions={tableOptions}
        paginationOptions={{
          ...paginationOptions,
          setPageNumber: (pageNumber) => {
            setSearchParams((params) => {
              params.set('page', (pageNumber + 1).toString());
              return params;
            });
            paginationOptions.setPageNumber(pageNumber);
          },
          setRowsPerPage: (rowsPerPage) => {
            setSearchParams((params) => {
              params.set('page_size', rowsPerPage.toString());
              return params;
            });
            paginationOptions.setRowsPerPage(rowsPerPage);
          },
        }}
        djangoResponse={data}
      >
        <TableBody>
          {data?.results.map((row) => (
            <TableRow key={row.batch_label}>
              <TableCell style={{ verticalAlign: 'middle' }}>
                {row.assay_type === 'assay_2018_tg' ? (
                  row.batch_label
                ) : (
                  <InternalLink
                    to={generatePath(QC_METRICS_BATCH_PATH, { batchLabel: row.batch_label })}
                  >
                    {row.batch_label}
                  </InternalLink>
                )}
              </TableCell>
              <TableCell style={{ verticalAlign: 'middle' }}>
                <Chip
                  label={row.assay_type}
                  variant="outlined"
                  size="medium"
                  sx={(theme) => ({
                    color: hashNameIntoPaletteColor(row.assay_type),
                    fontWeight: theme.typography.fontWeightBold,
                  })}
                />
              </TableCell>
              <TableCell style={{ verticalAlign: 'middle' }}>
                {renderCountDistribution(row.tag_stats, row.sample_sequence_run_count)}
              </TableCell>
              <TableCell style={{ verticalAlign: 'middle' }}>
                {renderCountDistribution(
                  row.state_counts,
                  row.sample_sequence_run_count,
                  partial(getStateColor, theme.palette)
                )}
              </TableCell>
              <TableCell style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                {row.on_hold_count > 0 && (
                  <Chip label={row.on_hold_count} variant="outlined" size="small" color="info" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DjangoTableWithPagination>
    </Container>
  );
};
